import React, { useEffect, useState, useRef, Children, isValidElement } from 'react';
import { invoke, delay, get, findIndex, some, includes } from 'lodash';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const TabHeadersRowStyle = css`
  display: flex;
  flex-direction: row;
  color: #535250;
  position: relative;

  @media (max-width: 641px) {
    justify-content: space-between;
  }
`;

const buttonStyle = css`
  font-family: 'Nunito Sans', sans-serif;
  font-style: normal;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  gap: 1rem;
  background: transparent;
  border: 0;
  min-width: 8rem;
  cursor: pointer;
  font-weight: 600 !important;
  color: rgb(42, 41, 40);
  position: relative;

  @media (max-width: 641px) {
    font-size: 0.875rem !important;
    padding: 1rem 0.25rem 0.5rem;
    line-height: 1.25rem !important;
    min-width: initial;
    display: flex;
    flex: 1;
    white-space: nowrap;
    justify-content: center;
  }

  @media (max-width: 350px) {
    font-size: 0.82rem !important;
    font-weight: 400 !important;
    padding: 1rem 0 0.5rem;
    line-height: 1.25rem !important;
    min-width: initial;
  }
`;

const disabledButtonStyle = css`
  color: #b0b0b0;
`;

const activeButtonStyle = css`
  color: #a7293a;
`;

const underlineStyle = css`
  background-color: #f1f0ee;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 0.125rem;
  border-radius: 0rem;
`;

const activeUnderlineStyle = css`
  background-color: #a7293a;
  z-index: 10;
`;

const TabContent = styled.div`
  max-height: ${(props: { maxHeight: number }) => (props.maxHeight ? `${props.maxHeight}rem` : 'initial')};
  overflow: hidden auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Tab = ({ children }) => {
  return <div>{children}</div>;
};

Tab.propTypes = {
  children: PropTypes.any,
  header: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

Tab.isTab = true;

const TabGroup = ({ children, startingTab = 0, contentMaxHeight, isLoading, paymentFailure = false }) => {
  const [activeTab, setActiveTab] = useState(startingTab);

  const headersRowRef = useRef(null);
  const activeTabHeaderRef = useRef(null);

  useEffect(() => {
    //select the first enabled tab
    const firstEnabledTab = findIndex(tabs, (tab) => get(tab, 'disabled') !== true);

    const hasDisabledTabs = some(children, (tab) => get(tab, 'props.disabled'));

    const hash = get(window, 'location.hash');
    if (hash === '#preferences') {
      const prefsTab = findIndex(children, {
        props: { header: 'Preferences' },
      });
      if (prefsTab !== -1 && !get(children, `${prefsTab}.props.disabled`)) {
        setActiveTab(prefsTab);
      } else {
        setActiveTab(firstEnabledTab);
      }
    }else if(paymentFailure) {
      const prefsTab = findIndex(children, {
        props: { header: 'Preferences' },
      });
      if (prefsTab !== -1  && !get(children, `${prefsTab}.props.disabled`) ) {
        setActiveTab(prefsTab); 
      }
    } else if (!isLoading && hasDisabledTabs) {
      setActiveTab(firstEnabledTab);
    }
  }, [isLoading]);

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  let tabs: Array<any> = [];
  Children.forEach(children, (child: { props: object }) => {
    if (isValidElement(child) && get(child, 'type.isTab')) {
      tabs.push({ ...child.props });
    }
  });

  const renderTab = () => {
    const { children } = tabs[activeTab];
    return (
      <>
        <div id={`tabpanel-${activeTab}`} role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
          {children}
        </div>
      </>
    );
  };

  const analyticsEvents = (type) => {
    try {
      if (window['AB']?.DATALAYER) {
        window['digitalData'].pageFlags = window?.['digitalData']?.pageFlags || {};
        if (type === 'Delivery') {
          window['AB'].DATALAYER.setModalClickEvents('ar:fulfillment-edit|delivery');
        } else if (type === 'Pickup') {
          window['AB'].DATALAYER.setModalClickEvents('ar:fulfillment-edit|pickup');
        } else if (type === 'Upcoming orders') {
          window['digitalData'].pageFlags.SNSInitPageload = true;
        } else if (type === 'Scheduled items' || type === 'Preferences') {
          window['digitalData'].pageFlags.SNSInitPageload = false;
        }
      }
    } catch (error) {
      // Handle any errors that occur during execution
      console.error('@AnalyticsTeam error:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (evt) => {
      if (includes(['ArrowLeft', 'ArrowRight'], evt.key)) {
        if (evt.key === 'ArrowLeft') {
          setActiveTab((prevTab) => {
            return prevTab > 0 ? prevTab - 1 : 0;
          });
        } else if (evt.key === 'ArrowRight') {
          setActiveTab((prevTab) => {
            return prevTab < Children.count(children) - 1 ? prevTab + 1 : prevTab;
          });
        }

        delay(() => invoke(activeTabHeaderRef, 'current.focus'), 0);
      }
    };

    invoke(headersRowRef, 'current.addEventListener', 'keydown', handleKeyDown);

    return () => {
      invoke(headersRowRef, 'current.removeEventListener', 'keydown', handleKeyDown);
    };
  }, [children]);

  return (
    <div className="tabgroup">
      <div className={TabHeadersRowStyle} ref={headersRowRef} role="tablist">
        {Children.map(children, (child, index) => {
          const header = get(child, 'props.header', '');
          const disabled = get(child, 'props.disabled', false);
          const isActive = activeTab === index;
          const tabIndex = isActive ? 0 : -1;

          return (
            <button
              className={`body-text ${buttonStyle} ${disabled ? disabledButtonStyle : ''} ${isActive ? activeButtonStyle : ''}`}
              role="tab"
              id={`tab-${index}`}
              aria-selected={isActive}
              ref={isActive ? activeTabHeaderRef : null}
              aria-controls={`tabpanel-${index}`}
              onClick={() => {
                handleTabClick(index);
                analyticsEvents(tabs[index].header);
              }}
              key={index}
              disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleTabClick(index);
                }
              }}
              tabIndex={tabIndex}
            >
              {header}
              {isActive ? <div className={`${underlineStyle} ${activeUnderlineStyle}`}></div> : null}
            </button>
          );
        })}
        <div className={underlineStyle}></div>
      </div>

      <TabContent maxHeight={contentMaxHeight} className="tab-content">
        {renderTab()}
      </TabContent>
    </div>
  );
};

TabGroup.propTypes = {
  children: PropTypes.any,
  contentMaxHeight: PropTypes.number,
  isLoading: PropTypes.bool,
  paymentFailure: PropTypes.bool,
};

export { Tab, TabGroup };
